import { useMutation } from '@apollo/client';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { UPDATE_PROD_ORDER } from '@retainerclub/admin-api';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import Button from '../../../../shared/Button/Button';
import ErrorMessage from '../../../../shared/ErrorMessage/ErrorMessage';
import SubmitButton from '../../../../shared/FormElements/Buttons/SubmitButton';
import FieldContainer from '../../../../shared/FormElements/FieldContainer/FieldContainer';
import RcSelect from '../../../../shared/FormElements/Selects/RcSelect';
import BaseLoader from '../../../../shared/Loader/BaseLoader';
import AddressFields from '../../../../shared/ShowElements/ShowCustomerAddresses/AccountAddressModal/AddressFields';

function initialValues(address = {}) {
  return {
    address: address?.address || null,
    address2: address?.address2 || null,
    city: address?.city || null,
    region: address?.state || null,
    postalCode: address?.postalCode || null,
    country: address?.country || 'US',
    selectedAccountAddressId: null,
  };
}

function schema() {
  return Yup.object().shape({
    address: Yup.string().required('address is required'),
    address2: Yup.string().nullable(true),
    city: Yup.string().required('city is required'),
    region: Yup.string().required('region / state is required'),
    postalCode: Yup.string().required('postal code is required'),
    country: Yup.string(),
    selectedAccountAddressId: Yup.string().nullable(true),
  });
}

function ProductionOrderAddressModalWrapper({
  account,
  address = null,
  handleCloseModal,
  partnerLocation,
  productionOrderId,
  refetch = null,
}) {
  const [mutationErrors, setMutationErrors] = useState(null);
  const [addressMode, setAddressMode] = useState('edit'); // 'partner', 'account', 'edit'

  const [update, { error, loading }] = useMutation(UPDATE_PROD_ORDER);

  const partnerShippingAddress = partnerLocation?.partnerShippingAddress;
  const accountAddresses = account?.accountAddresses || [];

  const accountAddressOptions = useMemo(() => {
    return accountAddresses
      .filter((addr) => addr.isShipping)
      .map((addr) => ({
        id: addr.id,
        name: `${addr.address}${addr.address2 ? `, ${addr.address2}` : ''}, ${addr.city}, ${addr.region} ${addr.postalCode}${addr.isDefault ? ' (Default)' : ''}`,
        value: addr.id,
      }));
  }, [accountAddresses]);

  const memoizedInitialValues = useMemo(
    () => initialValues(address),
    [address],
  );

  const {
    register,
    handleSubmit,
    formState: { isLoading, isSubmitting, isValid },
    formState,
    getValues,
    setValue,
    watch,
    control,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: memoizedInitialValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const selectedAccountAddressId = watch('selectedAccountAddressId');
  const watchedAddress = watch('address');
  const watchedAddress2 = watch('address2');
  const watchedCity = watch('city');
  const watchedRegion = watch('region');
  const watchedPostalCode = watch('postalCode');
  const watchedCountry = watch('country');

  // Watch for account address selection changes
  useEffect(() => {
    if (selectedAccountAddressId) {
      const selectedAddress = accountAddresses.find(
        (addr) => addr.id === selectedAccountAddressId,
      );
      if (selectedAddress) {
        setValue('address', selectedAddress.address);
        setValue('address2', selectedAddress.address2 || '');
        setValue('city', selectedAddress.city);
        setValue('region', selectedAddress.region);
        setValue('postalCode', selectedAddress.postalCode);
        setValue('country', selectedAddress.country);
        setAddressMode('account');
      }
    }
  }, [selectedAccountAddressId, accountAddresses, setValue]);

  // Helper function to normalize address object
  const normalizeAddress = useCallback((addr) => {
    if (!addr) {
      return null;
    }
    return {
      address: addr.address || '',
      address2: addr.address2 || '',
      city: addr.city || '',
      region: addr.region || '',
      postalCode: addr.postalCode || '',
      country: addr.country || '',
    };
  }, []);

  // Helper function to get expected address values based on mode
  const getExpectedAddressValues = useCallback(() => {
    if (addressMode === 'partner') {
      return normalizeAddress(partnerShippingAddress);
    }
    if (addressMode === 'account' && selectedAccountAddressId) {
      const selectedAddress = accountAddresses.find(
        (addr) => addr.id === selectedAccountAddressId,
      );
      return normalizeAddress(selectedAddress);
    }
    return null;
  }, [
    addressMode,
    partnerShippingAddress,
    selectedAccountAddressId,
    accountAddresses,
    normalizeAddress,
  ]);

  // Detect manual edits to address fields and reset mode to 'edit'
  useEffect(() => {
    // Skip if already in 'edit' mode
    if (addressMode === 'edit') {
      return;
    }

    const expectedValues = getExpectedAddressValues();
    if (!expectedValues) {
      return;
    }

    // Get current form values
    const currentValues = {
      address: watchedAddress || '',
      address2: watchedAddress2 || '',
      city: watchedCity || '',
      region: watchedRegion || '',
      postalCode: watchedPostalCode || '',
      country: watchedCountry || '',
    };

    // If values don't match, user manually edited - reset to 'edit' mode
    const hasManualEdit = Object.keys(expectedValues).some((key) => {
      const current = (currentValues[key] || '').trim();
      const expected = (expectedValues[key] || '').trim();
      return current !== expected;
    });

    if (hasManualEdit) {
      setAddressMode('edit');
    }
  }, [
    addressMode,
    watchedAddress,
    watchedAddress2,
    watchedCity,
    watchedRegion,
    watchedPostalCode,
    watchedCountry,
    getExpectedAddressValues,
  ]);

  const applyPartnerAddress = useCallback(() => {
    if (!partnerShippingAddress) {
      return;
    }

    setValue('address', partnerShippingAddress.address);
    setValue('address2', partnerShippingAddress.address2 || '');
    setValue('city', partnerShippingAddress.city);
    setValue('region', partnerShippingAddress.region);
    setValue('postalCode', partnerShippingAddress.postalCode);
    setValue('country', partnerShippingAddress.country);
    setValue('selectedAccountAddressId', null);
    setAddressMode('partner');
  }, [partnerShippingAddress, setValue]);

  async function submitRecord(values) {
    try {
      // Determine which address ID to use based on mode
      let addressId = null;
      let klass = null;

      if (addressMode === 'partner' && partnerShippingAddress?.id) {
        // Partner shipping address - use PartnerShippingAddress type
        addressId = partnerShippingAddress.id;
        klass = 'PartnerShippingAddress';
      } else if (addressMode === 'account' && values.selectedAccountAddressId) {
        // Selected account address
        addressId = values.selectedAccountAddressId;
        klass = 'AccountAddress';
      }
      // If addressMode is 'edit', addressId and klass remain null
      // This indicates the address was manually edited and is no longer
      // a reference to the original partner/account address

      // Convert form values to match ProductionOrder address JSONB structure
      const addressData = {
        id: addressId,
        klass: klass,
        address: values.address,
        address2: values.address2 || null,
        city: values.city,
        state: values.region,
        postalCode: values.postalCode,
        country: values.country,
      };

      const params = {
        variables: {
          input: {
            input: {
              id: productionOrderId,
              address: addressData,
            },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await update(params);

      // Guard against undefined data
      const mErrors = data?.updateProductionOrder?.errorMessages || [];

      if (mErrors.length > 0) {
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);
        if (refetch) {
          refetch();
        }
        handleCloseModal();
      }
    } catch (err) {
      console.error(err);
      // Surface error to user
      setMutationErrors([
        'An unexpected error occurred while updating the address. Please try again.',
      ]);
    }
  }

  if (loading) {
    return <BaseLoader title="Address" />;
  }

  return (
    <FormProvider
      {...rest}
      control={control}
      register={register}
      handleSubmit={handleSubmit}
      formState={formState}
      getValues={getValues}
      setValue={setValue}
    >
      <form onSubmit={handleSubmit(submitRecord)} noValidate>
        <div>
          <div className="space-y-4">
            {/* Info Warning */}
            <div className="rounded-lg border border-rcprimary-200 bg-rcprimary-50 p-4">
              <div className="flex">
                <div className="shrink-0">
                  <InformationCircleIcon
                    className="size-5 text-rcprimary-600"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <p className="font-medium text-rcprimary-800 text-sm">
                    Address changes apply only to this Production Order
                  </p>
                  <p className="mt-1 text-rcprimary-700 text-sm">
                    Editing the address here will only update this Production
                    Order's shipping address. If you need to update the actual
                    account address record, please edit it from the account page
                    first, then return here to select the updated address.
                  </p>
                </div>
              </div>
            </div>

            {/* Partner Shipping Address Option */}
            {partnerShippingAddress && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">
                      Partner Shipping Address
                    </p>
                    <p className="mt-1 text-gray-600 text-xs">
                      {partnerLocation?.name}
                    </p>
                    <p className="mt-1 text-gray-500 text-xs">
                      {partnerShippingAddress.address}
                      {partnerShippingAddress.address2 && (
                        <span>, {partnerShippingAddress.address2}</span>
                      )}
                      , {partnerShippingAddress.city},{' '}
                      {partnerShippingAddress.region}{' '}
                      {partnerShippingAddress.postalCode}
                    </p>
                  </div>
                  <Button
                    name="Use This Address"
                    size="sm"
                    color="rcprimary"
                    onClick={applyPartnerAddress}
                    type="button"
                  />
                </div>
              </div>
            )}

            {/* Account Addresses Selection */}
            {accountAddressOptions.length > 0 && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <FieldContainer
                  name="selectedAccountAddressId"
                  label="Select Account Address"
                >
                  <RcSelect
                    name="selectedAccountAddressId"
                    data={accountAddressOptions}
                    clearable
                    placeholder="Choose an account address..."
                  />
                </FieldContainer>
              </div>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-gray-300 border-t" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-2 text-gray-500">
                  Or edit address manually
                </span>
              </div>
            </div>

            {/* Free-form Address Fields */}
            <AddressFields />

            <div className="col-span-full">
              {error && (
                <div className="col-span-full mt-4">
                  <ErrorMessage error={error} />
                </div>
              )}

              {mutationErrors && (
                <div className="col-span-full mt-4">
                  <ErrorMessage errorMessages={mutationErrors} />
                </div>
              )}
              <div className="col-span-full mt-4 flex items-center justify-end gap-x-4">
                <SubmitButton
                  isLoading={isLoading || loading}
                  isSubmitting={isSubmitting}
                  isValid={isValid}
                  label="Update"
                  full
                  notRounded
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}

ProductionOrderAddressModalWrapper.propTypes = {
  account: PropTypes.shape({
    accountAddresses: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        region: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string,
        isShipping: PropTypes.bool,
        isDefault: PropTypes.bool,
      }),
    ),
  }),
  address: PropTypes.shape({
    id: PropTypes.string,
    klass: PropTypes.string,
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
  }),
  handleCloseModal: PropTypes.func.isRequired,
  partnerLocation: PropTypes.shape({
    name: PropTypes.string,
    partnerShippingAddress: PropTypes.shape({
      id: PropTypes.string,
      address: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      region: PropTypes.string,
      postalCode: PropTypes.string,
      country: PropTypes.string,
    }),
  }),
  productionOrderId: PropTypes.string.isRequired,
  refetch: PropTypes.func,
};

export default ProductionOrderAddressModalWrapper;
